import type {
  AddressWebApiDto,
  AppfolioRentalApplicationWebApiDto,
  ApplicationAnswerWebApiDto,
  ApplicationShareInformationWebApiDto,
  ApplicationShareWebApiDto,
  CoapplicantWebApiDto,
  CurrentIncomeAnswerDto,
  CurrentIncomeWebApiDto,
  EmployerAnswerDto,
  EmployerWebApiDto,
  GetListingForRentalApplicationWebApiResponseDto,
  GetRentalApplicationWebApiResponseDto,
  LocalDate,
  ManagerAnswerDto,
  ManagerWebApiDto,
  PastJobAnswerDto,
  PastJobWebApiDto,
  PetsWebApiDto,
  QuestionId,
  RentalApplicationAnswersDto,
  RentalApplicationListingWebApiDto,
  RentalApplicationWebApiDto,
  RentalListingSummaryWebApiDto,
  RequiredDocumentSectionWebApiDto,
  ResidenceAnswerDto,
  ResidenceWebApiDto,
  SaveAnswersWebApiRequestDto,
} from '@zg-rentals/ts-capps-client';
import { ApplicationSnapshotStatusType, ClearableAnswerField } from '@zg-rentals/ts-capps-client';
import type {
  Answers,
  AnswersInput,
  AppfolioRentalApplication,
  CurrentIncomeInput,
  CustomAnswerInput,
  Employer,
  ManagerContactInfo,
  PastJobInput,
  Pets,
  RentalApplication,
  RentalApplicationAddress,
  RentalApplicationListing,
  RequiredDocumentSection,
  Residence,
  ResidenceInput,
} from '../models/rental-application';
import { getNumberFromString } from './formatters';
import { isPreferredLeaseLength } from '@zg-rentals/applications-utils';

/**
 * Mappers between the capps web api DTOs and the view models in models/rental-application.ts.
 * The `to*` functions convert web api responses into the view model read shapes, and
 * `toSaveAnswersRequest` converts the `AnswersInput` write shape into the web api request.
 */

/** AnswersInput fields that clear their stored answer when set to null */
const CLEARABLE_FIELDS: Partial<Record<keyof AnswersInput, ClearableAnswerField>> = {
  backgroundReportComment: ClearableAnswerField.BACKGROUND_REPORT_COMMENT,
  creditReportComment: ClearableAnswerField.CREDIT_REPORT_COMMENT,
  currentResidence: ClearableAnswerField.CURRENT_RESIDENCE,
  firstName: ClearableAnswerField.FIRST_NAME,
  isLeaseLengthFlexible: ClearableAnswerField.IS_LEASE_LENGTH_FLEXIBLE,
  isMoveInDateFlexible: ClearableAnswerField.IS_MOVE_IN_DATE_FLEXIBLE,
  isSmoker: ClearableAnswerField.IS_SMOKER,
  lastName: ClearableAnswerField.LAST_NAME,
  middleName: ClearableAnswerField.MIDDLE_NAME,
  moveInDate: ClearableAnswerField.MOVE_IN_DATE,
  occupants: ClearableAnswerField.OCCUPANTS,
  pets: ClearableAnswerField.PETS,
  phone: ClearableAnswerField.PHONE,
  preferredLeaseLengths: ClearableAnswerField.PREFERRED_LEASE_LENGTHS,
  reasonForMoving: ClearableAnswerField.REASON_FOR_MOVING,
};

function toIsoDateTime(epochMs: number | undefined): string | null {
  return epochMs == null ? null : new Date(epochMs).toISOString();
}

/** LocalDate to the yyyy-MM-dd "server date" string format the form layer works with */
function toServerDate(date: LocalDate | undefined): string | null {
  if (!date) {
    return null;
  }
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.year}-${pad(date.month)}-${pad(date.day)}`;
}

export function toLocalDate(serverDate: string): LocalDate {
  const [year, month, day] = serverDate.split('-').map(Number);
  return { year, month, day };
}

/** Cents to the formatted amount the form layer works with, e.g. 190000 to '$1,900.00' */
function toUsCurrency(cents: number | undefined): string | null {
  return cents == null ? null : (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

function toCents(amount: unknown): number {
  return Math.round(getNumberFromString(String(amount)) * 100);
}

/**
 * The renter never sees an invited application as its own state, an invited application that
 * can be opened is in progress
 */
function toApplicantStatus(status: ApplicationSnapshotStatusType | undefined) {
  return status === ApplicationSnapshotStatusType.INVITED ? ApplicationSnapshotStatusType.PENDING : (status ?? null);
}

/**
 * Nested objects must map every field to an explicit null (like the old graphql api did) because
 * the form dirty-check JSON-diffs form values against these answers, and the form registers
 * inputs for every field. A key the web api omitted would otherwise diff against the form's
 * null and trigger a save on every step change.
 */
function toAddress(dto: AddressWebApiDto | undefined): RentalApplicationAddress | null {
  if (!dto) {
    return null;
  }
  return {
    street: dto.street ?? null,
    unit: dto.unit ?? null,
    city: dto.city ?? null,
    state: dto.state ?? null,
    zip: dto.zip ?? null,
  };
}

function toManager(dto: ManagerWebApiDto | undefined): ManagerContactInfo | null {
  if (!dto) {
    return null;
  }
  return {
    name: dto.name ?? null,
    email: dto.email ?? null,
    phone: dto.phone ?? null,
  };
}

function toEmployer(dto: EmployerWebApiDto | undefined): Employer | null {
  if (!dto) {
    return null;
  }
  return {
    name: dto.name ?? null,
    email: dto.email ?? null,
    phone: dto.phone ?? null,
    organization: dto.organization ?? null,
  };
}

function toPets(dto: PetsWebApiDto | undefined): Pets | null {
  if (!dto) {
    return null;
  }
  return {
    smallDogs: dto.smallDogs,
    mediumDogs: dto.mediumDogs,
    largeDogs: dto.largeDogs,
    cats: dto.cats,
    other: dto.other,
    description: dto.description ?? null,
  };
}

function toCurrentIncome(dto: CurrentIncomeWebApiDto) {
  return {
    id: dto.id,
    type: dto.type ?? null,
    title: dto.title ?? null,
    amount: toUsCurrency(dto.amountCents),
    note: dto.note ?? null,
    documents: dto.documents,
    employer: toEmployer(dto.employer),
    startDate: toServerDate(dto.startDate),
  };
}

function toRequiredDocumentSection(dto: RequiredDocumentSectionWebApiDto): RequiredDocumentSection {
  return {
    documentType: dto.documentType,
    incomeId: dto.incomeId ?? null,
    requestedBy: dto.requestedBy,
    parameterQuantity: dto.parameterQuantity ?? null,
    parameterUnit: dto.parameterUnit ?? null,
    documents: dto.documents,
  };
}

function toPastJob(dto: PastJobWebApiDto) {
  return {
    id: dto.id ?? null,
    title: dto.title ?? null,
    employer: toEmployer(dto.employer),
    startDate: toServerDate(dto.startDate),
    endDate: toServerDate(dto.endDate),
  };
}

function toResidence(dto: ResidenceWebApiDto): Residence {
  return {
    id: dto.id ?? null,
    type: dto.type ?? null,
    zpid: dto.zpid ?? null,
    address: toAddress(dto.address),
    startDate: toServerDate(dto.startDate),
    endDate: toServerDate(dto.endDate),
    monthlyPayment: toUsCurrency(dto.monthlyPaymentCents),
    manager: toManager(dto.manager),
    description: dto.description ?? null,
  };
}

function toCoapplicant(dto: CoapplicantWebApiDto) {
  return {
    id: dto.inviteId,
    email: dto.email ?? null,
    firstName: dto.firstName ?? null,
    lastName: dto.lastName ?? null,
    invitedDateTime: toIsoDateTime(dto.invitedDateTime),
  };
}

export function toAnswers(dto: ApplicationAnswerWebApiDto): Answers {
  return {
    id: dto.id,
    firstName: dto.firstName ?? null,
    middleName: dto.middleName ?? null,
    lastName: dto.lastName ?? null,
    phone: dto.phone ?? null,
    email: dto.email ?? null,
    moveInDate: toServerDate(dto.moveInDate),
    isMoveInDateFlexible: dto.isMoveInDateFlexible ?? null,
    preferredLeaseLengths: dto.preferredLeaseLengths ?? [],
    isLeaseLengthFlexible: dto.isLeaseLengthFlexible ?? null,
    occupants: dto.occupants ?? null,
    isSmoker: dto.isSmoker ?? null,
    backgroundReportComment: dto.backgroundReportComment ?? null,
    creditReportComment: dto.creditReportComment ?? null,
    reasonForMoving: dto.reasonForMoving ?? null,
    pets: toPets(dto.pets),
    currentIncomes: dto.currentIncomes.map(toCurrentIncome),
    pastJobs: dto.pastJobs.map(toPastJob),
    currentResidence: dto.currentResidence ? toResidence(dto.currentResidence) : null,
    pastResidences: dto.pastResidences.map(toResidence),
    coapplicants: dto.coapplicants.map(toCoapplicant),
    customAnswers: dto.customAnswers.map((answer) => ({ id: answer.questionId, value: answer.value })),
    // A capps deploy that predates the field omits the key
    requiredDocuments: (dto.requiredDocuments ?? []).map(toRequiredDocumentSection),
  };
}

function toRentalListing(dto: RentalApplicationListingWebApiDto) {
  return {
    listingAlias: dto.listingAlias,
    propertyUrl: dto.propertyUrl ?? null,
    isActive: dto.isActive,
    isApplicationsAccepted: dto.isApplicationsAccepted,
    isStreetEasy: dto.isStreetEasy,
    isBaseApplicationChangesEnabled: dto.isBaseApplicationChangesEnabled,
    address: dto.address
      ? {
          street: dto.address.street ?? null,
          unit: dto.address.unit ?? null,
          city: dto.address.city ?? null,
          state: dto.address.state ?? null,
          zip: dto.address.zip ?? null,
        }
      : null,
    bedrooms: dto.bedrooms ?? null,
    bathrooms: dto.bathrooms ?? null,
    squareFeet: dto.squareFeet ?? null,
    monthlyPayment: toUsCurrency(dto.monthlyPaymentCents),
    primaryPhoto: dto.primaryPhotoUrl ? { imageUrl: dto.primaryPhotoUrl } : null,
  };
}

export function toRentalApplicationListing(
  dto: GetListingForRentalApplicationWebApiResponseDto,
): RentalApplicationListing {
  return {
    rentalListing: dto.rentalListing ? toRentalListing(dto.rentalListing) : null,
    listingOwner: dto.listingOwner ?? null,
  };
}

function toShareInformation(dto: ApplicationShareWebApiDto) {
  return {
    shareDateTime: toIsoDateTime(dto.shareDateTime),
    status: dto.status ?? null,
    propertyId: dto.propertyAddress.propertyId,
    propertyAddress: {
      street: dto.propertyAddress.street,
      unit: dto.propertyAddress.unit,
      city: dto.propertyAddress.city,
      state: dto.propertyAddress.state,
      zip: dto.propertyAddress.zip,
    },
    landlordEmail: dto.landlordEmail ?? null,
  };
}

export function toRentalApplication(dto: GetRentalApplicationWebApiResponseDto): RentalApplication {
  return {
    __typename: 'RentalApplication',
    id: dto.id,
    status: toApplicantStatus(dto.applicantStatus),
    applicationStatus: dto.applicationStatus ?? null,
    role: dto.role,
    isPrimaryApplicant: dto.isPrimaryApplicant,
    ireneViewedDateTime: toIsoDateTime(dto.landlordViewedDateTime),
    startedByText: dto.startedByText ?? null,
    startedDateTime: toIsoDateTime(dto.startedDateTime),
    invitedDateTime: toIsoDateTime(dto.invitedDateTime),
    submittedDateTime: toIsoDateTime(dto.submittedDateTime),
    listingAlias: dto.listingAlias ?? null,
    receivedInvitations: dto.receivedInvitations.map((invitation) => ({
      id: invitation.snapshotId,
      rentalApplicationInviteId: invitation.inviteId,
      invitedBy: invitation.invitedBy ?? null,
    })),
    answers: dto.answers ? toAnswers(dto.answers) : null,
    statusHistory: dto.statusHistory.map((event) => ({ status: event.status, date: toIsoDateTime(event.date) })),
    applicationShareInformation: dto.applicationShareInformation
      ? toShareInformation(dto.applicationShareInformation)
      : null,
  };
}

function toListShareInformation(dto: ApplicationShareInformationWebApiDto) {
  return {
    shareDateTime: toIsoDateTime(dto.shareDateTime),
    status: dto.status,
    propertyId: dto.propertyId ?? null,
    propertyAddress: dto.propertyAddress ?? null,
    landlordEmail: dto.landlordEmail ?? null,
  };
}

function toListRentalListing(dto: RentalListingSummaryWebApiDto) {
  return {
    listingAlias: dto.listingAlias ?? null,
    propertyUrl: dto.propertyUrl ?? null,
    address: dto.address,
    primaryPhoto: dto.primaryPhoto.thumbUrl ? { thumbUrl: dto.primaryPhoto.thumbUrl } : null,
  };
}

export function toRentalApplicationListItem(dto: RentalApplicationWebApiDto): RentalApplication {
  return {
    __typename: 'RentalApplication',
    id: dto.id,
    isArchived: dto.isArchived,
    status: toApplicantStatus(dto.status),
    applicationStatus: dto.applicationStatus ?? null,
    isPrimaryApplicant: dto.isPrimaryApplicant,
    startedByText: dto.startedByText ?? null,
    startedDateTime: toIsoDateTime(dto.startedDateTime),
    invitedDateTime: toIsoDateTime(dto.invitedDateTime),
    submittedDateTime: toIsoDateTime(dto.submittedDateTime),
    invitedToMoveInDateTime: toIsoDateTime(dto.invitedToMoveInDateTime),
    withdrawnDateTime: toIsoDateTime(dto.withdrawnDateTime),
    rentalListing: dto.rentalListing ? toListRentalListing(dto.rentalListing) : null,
    applicationShareInformation: dto.applicationShareInformation
      ? toListShareInformation(dto.applicationShareInformation)
      : null,
  };
}

export function toAppfolioRentalApplication(dto: AppfolioRentalApplicationWebApiDto): AppfolioRentalApplication {
  return {
    __typename: 'AppfolioRentalApplication',
    id: dto.id,
    applicationUrl: dto.applicationUrl ?? null,
    listingAlias: dto.listingAlias ?? null,
    status: dto.status ?? null,
    startedDateTime: toIsoDateTime(dto.startedDateTime),
    approvedDateTime: toIsoDateTime(dto.approvedDateTime),
    declinedDateTime: toIsoDateTime(dto.declinedDateTime),
    submittedDateTime: toIsoDateTime(dto.submittedDateTime),
    withdrawnDateTime: toIsoDateTime(dto.withdrawnDateTime),
    address: dto.address ?? null,
    displayablePhotoUrl: dto.displayablePhotoUrl ?? null,
  };
}

function toEmployerAnswerDto(employer: CurrentIncomeInput['employer']): EmployerAnswerDto | undefined {
  if (!employer) {
    return undefined;
  }
  return {
    organization: employer.organization ?? undefined,
    name: employer.name ?? undefined,
    email: employer.email ?? undefined,
    phone: employer.phone ?? undefined,
  };
}

function toCurrentIncomeAnswerDto(income: CurrentIncomeInput): CurrentIncomeAnswerDto {
  return {
    id: income.id ?? undefined,
    type: income.type,
    title: income.title ?? undefined,
    amountCents: income.amount == null ? undefined : toCents(income.amount),
    note: income.note ?? undefined,
    startDate: income.startDate ? toLocalDate(income.startDate) : undefined,
    employer: toEmployerAnswerDto(income.employer),
  };
}

function toPastJobAnswerDto(job: PastJobInput): PastJobAnswerDto {
  return {
    id: job.id ?? undefined,
    title: job.title ?? undefined,
    startDate: job.startDate ? toLocalDate(job.startDate) : undefined,
    endDate: job.endDate ? toLocalDate(job.endDate) : undefined,
    employer: toEmployerAnswerDto(job.employer),
  };
}

function toManagerAnswerDto(manager: ResidenceInput['manager']): ManagerAnswerDto | undefined {
  if (!manager) {
    return undefined;
  }
  return {
    name: manager.name ?? undefined,
    email: manager.email ?? undefined,
    phone: manager.phone ?? undefined,
  };
}

function toResidenceAnswerDto(residence: ResidenceInput): ResidenceAnswerDto {
  return {
    id: residence.id ?? undefined,
    type: residence.type,
    zpid: residence.zpid == null ? undefined : Number(residence.zpid),
    address: residence.address
      ? {
          street: residence.address.street ?? undefined,
          city: residence.address.city ?? undefined,
          state: residence.address.state ?? undefined,
          zip: residence.address.zip == null ? undefined : String(residence.address.zip),
        }
      : undefined,
    startDate: residence.startDate ? toLocalDate(residence.startDate) : undefined,
    endDate: residence.endDate ? toLocalDate(residence.endDate) : undefined,
    monthlyPaymentCents: residence.monthlyPayment == null ? undefined : toCents(residence.monthlyPayment),
    manager: toManagerAnswerDto(residence.manager),
    description: residence.description ?? undefined,
  };
}

function toPetsAnswerDto(pets: NonNullable<AnswersInput['pets']>): RentalApplicationAnswersDto['pets'] {
  return {
    smallDogs: Number(pets.smallDogs ?? 0),
    mediumDogs: Number(pets.mediumDogs ?? 0),
    largeDogs: Number(pets.largeDogs ?? 0),
    cats: Number(pets.cats ?? 0),
    other: Number(pets.other ?? 0),
    description: pets.description ?? undefined,
  };
}

export function toSaveAnswersRequest(applicationId: string, input: AnswersInput): SaveAnswersWebApiRequestDto {
  const clearedFields: Array<ClearableAnswerField> = [];
  const answers: RentalApplicationAnswersDto = {};

  for (const [key, value] of Object.entries(input) as Array<[keyof AnswersInput, AnswersInput[keyof AnswersInput]]>) {
    if (value == null) {
      const clearedField = CLEARABLE_FIELDS[key];
      if (clearedField) {
        clearedFields.push(clearedField);
      }
      continue;
    }
    switch (key) {
      case 'firstName':
      case 'middleName':
      case 'lastName':
      case 'phone':
      case 'reasonForMoving':
      case 'backgroundReportComment':
      case 'creditReportComment':
        answers[key] = String(value);
        break;
      case 'moveInDate':
        answers.moveInDate = toLocalDate(String(value));
        break;
      case 'isMoveInDateFlexible':
      case 'isLeaseLengthFlexible':
      case 'isSmoker':
        answers[key] = Boolean(value);
        break;
      case 'preferredLeaseLengths':
        answers.preferredLeaseLengths = (value as NonNullable<AnswersInput['preferredLeaseLengths']>).filter(
          isPreferredLeaseLength,
        );
        break;
      case 'occupants':
        answers.occupants = Number(value);
        break;
      case 'pets':
        answers.pets = toPetsAnswerDto(value as NonNullable<AnswersInput['pets']>);
        break;
      case 'currentIncomes':
        answers.currentIncomes = (value as AnswersInput['currentIncomes'])
          ?.filter((income): income is CurrentIncomeInput => income != null)
          .map(toCurrentIncomeAnswerDto);
        break;
      case 'pastJobs':
        answers.pastJobs = (value as AnswersInput['pastJobs'])
          ?.filter((job): job is PastJobInput => job != null)
          .map(toPastJobAnswerDto);
        break;
      case 'currentResidence':
        answers.currentResidence = toResidenceAnswerDto(value as ResidenceInput);
        break;
      case 'pastResidences':
        answers.pastResidences = (value as AnswersInput['pastResidences'])
          ?.filter((residence): residence is ResidenceInput => residence != null)
          .map(toResidenceAnswerDto);
        break;
      case 'customAnswers':
        answers.customAnswers = (value as AnswersInput['customAnswers'])
          ?.filter((answer): answer is CustomAnswerInput => answer != null)
          .map((answer) => ({ questionId: answer.id as QuestionId, value: answer.value === true }));
        break;
    }
  }

  return {
    applicationId,
    answers,
    clearedFields: clearedFields.length > 0 ? clearedFields : undefined,
  };
}
